/**
 * My schedule (DNKADM-0363) — *their own world, the day's bookings*, on one screen.
 *
 * A signed-in staff member whose login is linked to a coach record (DNKADM-0362's
 * `authUserId`) sees their working hours, their locations, their date exceptions and
 * their bookings. There is **no new dink-api view**: the whole screen is composed
 * CLIENT-SIDE from reads dink-api already opens to a STAFF session, plus
 * ONE resolver that answers *which coach am I*:
 *
 *  1. `GET /v2/me/staff` (`getMyStaff`, DNKAPI-0520 / handoff §5) → the caller's own
 *     coach row. The app's ONLY `/v2/me/**` read — `POST /me/verify-password` is the
 *     v1 `/me/**` surface, a different predicate (`api/staff.ts` header);
 *  2. then, all `/v2/admin/**` and all STAFF-readable: `…/staff/{id}/location-assignments`,
 *     `…/staff/{id}/schedule?locationId=…`, `…/staff/{id}/date-exceptions`, and
 *     `GET /v2/admin/reservations?staffId=&from=&to=`.
 *
 * **The bookings come from the STAFF-SCOPED reservation list, never from the location
 * board filtered client-side.** The location list is scoped by ASSIGNMENT — a coach's
 * bookings and a location's bookings are different sets, and filtering one into the
 * other would show the wrong day. `staffId`/`from`/`to` are the server's own parameter
 * names (they differ from the location list's `locationId`/`start`/`end` deliberately —
 * `api/reservations.ts`).
 *
 * **DNKADM-0370 / DNKAPI-0525 — one server-scoped bookings call.** A missing
 * `locationId` now means every location in the caller's scope, so bookings are keyed
 * only on the linked `staffId` and date window. Assigned locations still drive the
 * header and hours tabs, but their availability never delays or fails this read.
 *
 * **Days off are self-service; everything else is a view (DNKADM-0375).** The caller adds
 * and removes date exceptions on their OWN coach record through the Staff-detail panel in
 * its editing mode — the same `POST`/`DELETE …/staff/{id}/date-exceptions` the Staff screen
 * uses, which dink-api opens to `STAFF_ROLES` and confines to the caller's own record
 * (`StaffSelfServiceGuard`, `403 NOT_OWN_STAFF_RECORD` otherwise). A booking opens the
 * read-only reservation detail (`canWrite={false}`).
 *
 * **Weekly hours follow the tenant switch (DNKADM-0490 / DNKAPI-0708).** `GET
 * /v2/admin/tenant-settings` (STAFF-readable) carries `coachesSetOwnHours`. On an explicit
 * `true` the hours render in the SAME editable grid the Staff screen uses, saving one
 * location at a time (the grid's one "Save schedule" PUTs the whole week for the open tab,
 * exactly as Staff detail does) through the coach's own `PUT /v2/me/staff/schedule` — no
 * staff id on the wire. Off, unreadable or failed → the read-only view and the "an owner
 * or admin sets these" copy, as before (fail-closed; the server refuses the write anyway).
 * A save that leaves bookings outside the new hours has still saved: the screen lists them
 * as a warning ("N bookings fall outside your new hours"), never a block, and nothing is
 * moved or cancelled.
 *
 * **Three answers, each rendered honestly** — all three are the SERVER's, not the
 * client's: `200` → the view; `404 STAFF_NOT_LINKED` → one empty state and **no
 * follow-up requests**; `403 NOT_TENANT_STAFF` (a platform-support session, which
 * holds no roster row) → the forbidden state. The nav leaf is hidden for a support
 * session (DNKADM-0356 / `NavLeaf.selfScoped`), but hiding a leaf is not
 * authorization: this route is ungated, and a typed URL lands here and gets that
 * `403` said plainly.
 *
 * The coach id is resolved on every screen open, in memory — never cached, never
 * stored.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { useListUrlState } from '@/components/list/useListUrlState';
import {
  getMyStaff,
  getStaffLocations,
  putMyStaffSchedule,
  getStaffSchedule,
  isStaffNotLinked,
  listStaffDateExceptions,
  staffDisplayName,
  type V2Staff,
} from '@/api/staff';
import { listLocations, type V2Location } from '@/api/locations';
import {
  listReservationWindow,
  truncatedWindowMessage,
  type BookingConflict,
  type Reservation,
} from '@/api/reservations';
import { getTenantSettings } from '@/api/tenantSettings';
import { scrubBrand } from '@/utils/whitelabel';
import { clock12 } from '@/utils/clockTime';
import { StaffScheduleGrid } from '@/features/staff/StaffScheduleGrid';
import { StaffDateExceptionsPanel } from '@/features/staff/StaffDateExceptionsPanel';
import { whenAndWhere, whoAndWhat } from '@/features/staff/bookingLines';
import { myScheduleSaveErrorMessage } from '@/features/staff/staffError';
import { ReservationDrawer } from '@/features/reservations/calendar/ReservationDrawer';
import { ScreenError } from '@/components/ScreenError';
import { addDays, dayEndIso, dayStartIso, todayIn, zoneOrDevice } from '@/components/list/listDate';
import { useCalendarZone } from '@/location/useCalendarZone';

/**
 * The booking window: **today through +7 days** (Operator ruling, DNKADM-0363
 * Decision Point 3). A day view would hide tomorrow's first booking from someone
 * checking their week tonight; a month would bury today's. Computed once per calendar
 * zone from the LOCATION's day boundaries (DNKADM-0439; `null` is the device) and
 * serialized as ISO-8601 instants — the same shape the location list's window uses.
 */
const WINDOW_DAYS = 7;

/**
 * DNKADM-0430: the weekly-hours location tab lives in the URL (`?location=<id>`, replaced,
 * not pushed). There is no sort: the bookings stay grouped by date, then start time.
 */
const URL_DEFAULTS = { location: '' };

function scheduleWindow(zone: string | null): { from: string; to: string } {
  const resolved = zoneOrDevice(zone);
  const today = todayIn(resolved);
  return {
    from: dayStartIso(today, resolved) ?? '',
    to: dayEndIso(addDays(today, WINDOW_DAYS), resolved) ?? '',
  };
}

/**
 * Split an ISO-8601-with-offset instant into its date and clock parts **as-is**,
 * without converting to the viewer's timezone — the reservation-rendering convention
 * this app follows everywhere (the stored offset is the venue's own). `null` when the
 * string is absent or not in the expected shape.
 */
function isoParts(iso: string | null): { date: string; clock: string } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  // The clock is 12-hour (DNKADM-0386 U2) through the shared formatter; still read off
  // the string as written — no offset conversion.
  return { date: `${m[1]}-${m[2]}-${m[3]}`, clock: clock12(Number(m[4]), Number(m[5])) };
}

/** The bookings grouped by their (as-is) start date, each group in start order. */
function groupByDate(reservations: Reservation[]): Array<{ date: string; rows: Reservation[] }> {
  const groups = new Map<string, Reservation[]>();
  for (const reservation of reservations) {
    const date = isoParts(reservation.start)?.date ?? 'Unscheduled';
    const rows = groups.get(date);
    if (rows) rows.push(reservation);
    else groups.set(date, [reservation]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, rows]) => ({
      date,
      rows: [...rows].sort((a, b) => (a.start ?? '').localeCompare(b.start ?? '')),
    }));
}

/** The one-line summary under a booking's contact name. */
function bookingMeta(reservation: Reservation): string {
  return (
    scrubBrand(
      [reservation.serviceName, reservation.assignedResourceName].filter(Boolean).join(' · '),
    ) ?? ''
  );
}

export function MyScheduleScreen() {
  // Which coach am I? Resolved on every open, in memory — never cached, never stored.
  const me = useResource(useCallback(() => getMyStaff(), []), []);

  // The linked coach row, or null for every other answer (loading, not linked,
  // forbidden, failed). EVERY read below is keyed on it, so the not-linked and
  // forbidden states issue no follow-up request at all.
  const staff: V2Staff | null =
    me.status === 'ready' && me.data !== null && !isStaffNotLinked(me.data) ? me.data : null;
  const staffId = staff?.id ?? null;

  // The coach's assigned locations, resolved to full rows for the tab labels and the
  // exception location labels (the same two-read resolve StaffDetail does).
  const assigned = useResource(
    useCallback(async (): Promise<V2Location[] | null> => {
      if (staffId === null) return null;
      const [ids, all] = await Promise.all([
        getStaffLocations(staffId),
        listLocations({ status: 'active' }),
      ]);
      const order = new Map(all.map((l, i) => [l.id, i] as const));
      return all
        .filter((l) => ids.includes(l.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [staffId]),
    [staffId],
  );

  const exceptions = useResource(
    useCallback(
      () => (staffId === null ? Promise.resolve(null) : listStaffDateExceptions(staffId)),
      [staffId],
    ),
    [staffId],
  );

  // The active schedule tab = the selected assigned location id (schedules are
  // authored per (staff, location), handoff §5.6). Read from the URL; an id that is not
  // one of the coach's assigned locations falls back to the first tab below.
  const { values: urlValues, set: setUrl } = useListUrlState(URL_DEFAULTS);
  const activeLocationId = urlValues.location === '' ? null : urlValues.location;
  const setActiveLocationId = (id: string) => setUrl({ location: id });
  const assignedData = assigned.data;
  const assignedLocations = useMemo(() => assignedData ?? [], [assignedData]);
  const effectiveLocationId = useMemo(() => {
    if (activeLocationId !== null && assignedLocations.some((l) => l.id === activeLocationId)) {
      return activeLocationId;
    }
    return assignedLocations[0]?.id ?? null;
  }, [activeLocationId, assignedLocations]);

  const schedule = useResource(
    useCallback(
      () =>
        staffId === null || effectiveLocationId === null
          ? Promise.resolve(null)
          : getStaffSchedule(staffId, effectiveLocationId),
      [staffId, effectiveLocationId],
    ),
    [staffId, effectiveLocationId],
  );

  // The booking window is fixed per calendar zone, so a re-render can never slide it (and
  // re-fetch) under the operator; it moves at most once, when the locations load.
  const calendarZone = useCalendarZone();
  const range = useMemo(() => scheduleWindow(calendarZone), [calendarZone]);
  // DNKAPI-0525 scopes an omitted `locationId` to every location the caller may
  // access. This read therefore depends only on the linked coach and its fixed
  // window; assignment loading/failure is independent UI state for hours/header.
  const bookings = useResource(
    useCallback(
      () =>
        staffId === null
          ? Promise.resolve(null)
          : listReservationWindow({ staffId, from: range.from, to: range.to }),
      [staffId, range.from, range.to],
    ),
    [staffId, range.from, range.to],
  );

  // DNKADM-0490: may this coach edit their own weekly hours? Read only once linked (the
  // not-linked / forbidden states issue no follow-ups). Only an explicit `true` edits.
  const settings = useResource(
    useCallback(
      () => (staffId === null ? Promise.resolve(null) : getTenantSettings()),
      [staffId],
    ),
    [staffId],
  );
  const canEditHours = settings.status === 'ready' && settings.data?.coachesSetOwnHours === true;

  // The bookings the last successful save left outside the new hours, per location. A
  // warning only — the save has already persisted.
  const [outsideHours, setOutsideHours] = useState<{
    locationId: string;
    bookings: BookingConflict[];
  } | null>(null);

  const [selected, setSelected] = useState<Reservation | null>(null);
  const showToast = useToast();

  const notLinked = me.status === 'ready' && me.data !== null && isStaffNotLinked(me.data);

  if (me.status === 'loading') {
    return (
      <Box>
        <MyScheduleHeading />
        <Skeleton
          variant="rounded"
          height={160}
          role="status"
          aria-label="Loading my schedule"
          sx={{ borderRadius: 3, maxWidth: 640, mt: 2 }}
        />
      </Box>
    );
  }

  if (notLinked) {
    return (
      <Box>
        <MyScheduleHeading />
        {/* One empty state, and no retry: an unlinked login is a correct, stable
            answer that only an owner can change — a Retry button would invite the
            operator to knock on a door that is not theirs to open. */}
        <Alert severity="info" sx={{ maxWidth: 620, mt: 2 }}>
          Your login isn’t linked to a coach record yet. Ask an owner to link it in Set up ›
          Staff.
        </Alert>
      </Box>
    );
  }

  if (staff === null) {
    return (
      <Box>
        <MyScheduleHeading />
        {/* A `403` here means one thing: the session is not a tenant staff member
            (`NOT_TENANT_STAFF` — a platform-support token holds no roster row). The
            shared access notice says so (DNKADM-0440), with no retry. */}
        <ScreenError
          error={me.error}
          what="your schedule"
          onRetry={me.refetch}
          forbiddenMessage="My schedule shows a coach their own hours and bookings. This session isn’t a staff member of this tenant, so there is no schedule to show."
          sx={{ maxWidth: 620, mt: 2 }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <MyScheduleHeading />

      <Typography variant="h6" component="p" sx={{ mt: 1.5 }}>
        {staffDisplayName(staff)}
      </Typography>
      {assigned.status === 'ready' && (
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {assignedLocations.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You aren’t assigned to any locations yet.
            </Typography>
          ) : (
            assignedLocations.map((loc) => (
              <Chip key={loc.id} size="small" variant="outlined" label={loc.name || loc.id} />
            ))
          )}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      {/* ---- Weekly hours (editable when the tenant lets coaches set them, DNKADM-0490) ---- */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Weekly hours
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {canEditHours
          ? 'Your working hours at each location, in that location’s local time. Set them for one location at a time and save. Existing bookings are never moved or cancelled.'
          : 'Your working hours at each location, in that location’s local time. An owner or admin sets these on the Staff screen.'}
      </Typography>

      {assigned.status === 'loading' && (
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3, maxWidth: 640 }} />
      )}
      {assigned.status === 'error' && (
        <ScreenError
          error={assigned.error}
          what="your locations"
          onRetry={assigned.refetch}
          sx={{ maxWidth: 620, mb: 4, alignItems: 'center' }}
        />
      )}

      {assignedLocations.length > 0 && effectiveLocationId !== null && (
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={effectiveLocationId}
            onChange={(_e, value: string) => setActiveLocationId(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
            aria-label="Schedule location"
          >
            {assignedLocations.map((loc) => (
              <Tab key={loc.id} value={loc.id} label={loc.name === '' ? loc.id : loc.name} />
            ))}
          </Tabs>

          {(schedule.status === 'loading' || settings.status === 'loading') && (
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3, maxWidth: 640 }} />
          )}
          {schedule.status === 'error' && (
            <ScreenError
              error={schedule.error}
              what="your hours for this location"
              onRetry={schedule.refetch}
              sx={{ maxWidth: 620, alignItems: 'center' }}
            />
          )}
          {schedule.status === 'ready' &&
            schedule.data &&
            settings.status !== 'loading' &&
            (canEditHours ? (
              <StaffScheduleGrid
                // Remount per location / per (re)load so the rows reseed.
                key={`myhours-edit|${effectiveLocationId}`}
                days={schedule.data.days}
                staffId={staff.id}
                locationId={effectiveLocationId}
                save={async (days) => {
                  const locationId = effectiveLocationId;
                  setOutsideHours(null);
                  const result = await putMyStaffSchedule(locationId, days);
                  setOutsideHours({ locationId, bookings: result.bookingsOutsideHours });
                }}
                errorMessage={myScheduleSaveErrorMessage}
                refetch={schedule.refetch}
                showToast={showToast}
              />
            ) : (
              <StaffScheduleGrid
                // Remount per location / per (re)load so the rows reseed.
                key={`myhours|${effectiveLocationId}`}
                days={schedule.data.days}
                readOnly
              />
            ))}
          {canEditHours &&
            outsideHours !== null &&
            outsideHours.locationId === effectiveLocationId &&
            outsideHours.bookings.length > 0 && (
              <OutsideHoursWarning bookings={outsideHours.bookings} />
            )}
        </Box>
      )}

      {/* ---- Date exceptions (self-service, DNKADM-0375) ---- */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Date exceptions
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Days you are off, or working special hours. You can add and remove these yourself.
      </Typography>
      {exceptions.status === 'loading' && (
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3, maxWidth: 640, mb: 4 }} />
      )}
      {exceptions.status === 'error' && (
        <ScreenError
          error={exceptions.error}
          what="your date exceptions"
          onRetry={exceptions.refetch}
          sx={{ maxWidth: 620, mb: 4, alignItems: 'center' }}
        />
      )}
      {exceptions.status === 'ready' && exceptions.data && (
        <Box sx={{ mb: 4 }}>
          <StaffDateExceptionsPanel
            exceptions={exceptions.data}
            staffId={staff.id}
            locations={assignedLocations}
            refetch={exceptions.refetch}
            showToast={showToast}
          />
        </Box>
      )}

      {/* ---- Bookings ---- */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Your bookings
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Everything booked with you from today through the next {WINDOW_DAYS} days, at every
        location.
      </Typography>
      {bookings.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={160}
          role="status"
          aria-label="Loading your bookings"
          sx={{ borderRadius: 3, maxWidth: 640 }}
        />
      )}
      {bookings.status === 'error' && (
        <ScreenError
          error={bookings.error}
          what="your bookings"
          onRetry={bookings.refetch}
          sx={{ maxWidth: 620, alignItems: 'center' }}
        />
      )}
      {bookings.status === 'ready' && bookings.data?.truncated === true && (
        <Alert severity="warning" sx={{ maxWidth: 620, mb: 1.5 }} data-testid="schedule-truncated">
          {truncatedWindowMessage(bookings.data.reservations.length)}
        </Alert>
      )}
      {bookings.status === 'ready' && bookings.data && (
        <BookingsList bookings={bookings.data.reservations} onSelect={setSelected} />
      )}

      {/* The existing reservation detail, read-only for everyone here (DNKADM-0361's
          `canWrite={false}`): My schedule is a view, so the footer actions, the
          reassign pencils and the money actions are all absent. Mounted only on a
          selection, so nothing behind the drawer is read until a booking is opened. */}
      {selected !== null && (
        <ReservationDrawer
          reservation={selected}
          canWrite={false}
          onClose={() => setSelected(null)}
          // Every write callback is unreachable with `canWrite={false}` — the controls
          // that raise them are not rendered. They are required props, so they are
          // supplied as no-ops rather than as writes this screen does not offer.
          onReschedule={() => undefined}
          onCancel={() => undefined}
          onCheckIn={() => undefined}
          onNoShow={() => undefined}
          onConfirm={() => undefined}
          onReassign={() => undefined}
          onEditTime={() => undefined}
          onChangeAssignment={() => undefined}
          onPendingChargeChanged={() => Promise.resolve()}
        />
      )}
    </Box>
  );
}

/** The screen heading, shared by every state so the page never renders headless. */
function MyScheduleHeading() {
  return (
    <Typography variant="h1" component="h1">
      My schedule
    </Typography>
  );
}

/**
 * The post-save warning (DNKADM-0490): the hours ARE saved, and these bookings now fall
 * outside them. Nothing was moved or cancelled; the list is for the coach to follow up.
 */
function OutsideHoursWarning({ bookings }: { bookings: BookingConflict[] }) {
  const n = bookings.length;
  return (
    <Alert severity="warning" sx={{ maxWidth: 620, mt: 2 }} data-testid="outside-hours-warning">
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        {n === 1
          ? '1 booking falls outside your new hours'
          : `${n} bookings fall outside your new hours`}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Your hours are saved. These bookings haven’t been moved or cancelled.
      </Typography>
      <Stack
        component="ul"
        aria-label="Bookings outside your new hours"
        sx={{ listStyle: 'none', p: 0, m: 0, gap: 0.75 }}
      >
        {bookings.map((b, index) => (
          <Box component="li" key={b.reservationUuid ?? `outside-${index}`}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{whoAndWhat(b)}</Typography>
            <Typography variant="body2">{whenAndWhere(b)}</Typography>
          </Box>
        ))}
      </Stack>
    </Alert>
  );
}

/** The day-grouped booking rows; each row opens the read-only detail. */
function BookingsList({
  bookings,
  onSelect,
}: {
  bookings: Reservation[];
  onSelect: (reservation: Reservation) => void;
}) {
  const t = resolveTokens(useTheme());
  const groups = useMemo(() => groupByDate(bookings), [bookings]);

  if (groups.length === 0) {
    return (
      <Paper elevation={1} sx={{ px: 2.75, py: 2.5, maxWidth: 640 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Nothing booked with you in this window.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack sx={{ gap: 2, maxWidth: 640 }}>
      {groups.map((group) => (
        <Box key={group.date}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{group.date}</Typography>
          <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
            {group.rows.map((reservation, index) => {
              const name = scrubBrand(reservation.contactName) ?? 'Reservation';
              const clock = isoParts(reservation.start)?.clock ?? '—';
              return (
                <Stack
                  key={reservation.reservationUuid ?? `${group.date}-${index}`}
                  component="button"
                  type="button"
                  onClick={() => onSelect(reservation)}
                  aria-label={`Open ${name} at ${clock} on ${group.date}`}
                  direction="row"
                  sx={{
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    minHeight: 52,
                    px: 0,
                    py: 1.25,
                    border: 'none',
                    borderBottom: index === group.rows.length - 1 ? 'none' : '1px solid',
                    borderColor: t.lineFaint,
                    bgcolor: 'transparent',
                  }}
                >
                  <Typography
                    variant="mono"
                    sx={{ fontSize: 12.5, color: 'text.secondary', width: 56, flexShrink: 0 }}
                  >
                    {clock}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                      {name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                      {bookingMeta(reservation)}
                    </Typography>
                  </Box>
                  <Icon name="chevron_right" sx={{ fontSize: 20, color: 'text.secondary' }} />
                </Stack>
              );
            })}
          </Paper>
        </Box>
      ))}
    </Stack>
  );
}
